/**
 * Kiểu của module `bao-cao` (Báo cáo doanh nghiệp), chép đúng hợp đồng ở `modules/bao-cao/README.md`
 * (mục "Định dạng dây", "DocumentItem", bảng API).
 *
 * Module **giữ định dạng của erp-service native** vì app di động đã chốt hợp đồng bằng `zod`
 * (ADR-0081 mục 3): camelCase, khoá vắng thay cho `null` (trừ `nextCursor`, `parentId`), danh sách tài
 * liệu `{items, total, nextCursor}`, thư mục và nguồn trả mảng trần.
 *
 * Sáu enum dưới đây là ENUM ĐÓNG (`backend/src/hop-dong/enum.ts`): giá trị lạ làm app di động vỡ cả
 * trang, và backend trả 400 ở đường ghi. Giao diện chỉ cho chọn trong danh sách, không cho gõ tự do.
 */

export const MODULE_IDS = ['overview', 'finance', 'projects', 'sales', 'people'] as const;
export type ModuleId = (typeof MODULE_IDS)[number];

export const CATEGORIES = ['finance', 'projects', 'sales', 'people', 'other'] as const;
export type Category = (typeof CATEGORIES)[number];

export const DOC_TYPES = ['pdf', 'web', 'document', 'unknown'] as const;
export type DocType = (typeof DOC_TYPES)[number];

export const TRENDS = ['up', 'down', 'flat'] as const;
export type Trend = (typeof TRENDS)[number];

export const ACCENT_NAMES = ['primary', 'success', 'warning', 'danger', 'violet', 'teal', 'sky', 'orange'] as const;
export type AccentName = (typeof ACCENT_NAMES)[number];

export const ICON_NAMES = [
  'banknote', 'receipt', 'droplet', 'trending-up',
  'wallet', 'chart-column', 'folder', 'clipboard-list',
  'calendar', 'users', 'user-check', 'briefcase',
  'target', 'handshake', 'package', 'file-text',
] as const;
export type IconName = (typeof ICON_NAMES)[number];

/** Slug phân hệ trên URL (`/bao-cao-doanh-nghiep/tai-chinh`), cũng là tên tài nguyên quyền. */
export const SLUG_PHAN_HE = ['tong-quan', 'tai-chinh', 'du-an', 'kinh-doanh', 'nhan-su'] as const;
export type SlugPhanHe = (typeof SLUG_PHAN_HE)[number];

export interface Hero {
  label: string;
  /** Chuỗi đã định dạng (`8,42 tỷ`). App hiện nguyên văn, không tự quy đổi. */
  value: string;
  caption?: string;
  delta?: string;
  /** Trường riêng, không suy từ dấu của `delta`: `-4,1%` ở Công nợ là tin tốt. */
  trend: Trend;
  /** Số thô cùng đơn vị với `value`, tối thiểu 2 điểm, điểm cuối khớp `value`. */
  spark?: number[];
}

export interface Stat {
  id: string;
  label: string;
  value: string;
  delta?: string;
  trend: Trend;
  accentName: AccentName;
  iconName: IconName;
}

export interface RankRow {
  id: string;
  label: string;
  value: string;
}

export interface Rank {
  title: string;
  rows: RankRow[];
}

/** Payload phân hệ (`GET /modules/{moduleId}`). Chưa nhập số liệu thì backend trả `404`. */
export interface ModulePayload {
  id: ModuleId;
  title: string;
  hero?: Hero;
  stats?: Stat[];
  documentsTitle?: string;
  /** Tổng thật trong kho, không phải `documents.length`. */
  documentsTotal?: number;
  documents?: DocumentItem[];
  rank?: Rank;
  updatedAt?: string;
}

/**
 * Thân `PUT /modules/{moduleId}` — ghi đè trọn gói. Khối vắng là **xoá khối** (kể cả `stats`, khác
 * native), nên màn sửa luôn gửi đủ mọi khối đang có.
 */
export interface UpdateModulePayload {
  title: string;
  documentsTitle?: string;
  documentCategories?: Category[];
  documentTake?: number;
  hero?: Hero & { spark: number[] };
  stats?: Stat[];
  rank?: Rank;
}

export interface DocumentItem {
  id: string;
  title: string;
  /** Liên kết ngoài: URL gốc. Tài liệu có tệp: địa chỉ cố định `…/documents/{id}/open`. */
  url: string;
  type: DocType;
  summary?: string;
  meta?: string;
  thumbnail?: string;
  iconName: IconName;
  accentName: AccentName;
  category: Category;
  folderId?: string;
  /** Có ở tài liệu có tệp tải lên. */
  fileName?: string;
  fileSizeBytes?: number;
  /** Mốc nghiệp vụ, dùng để sắp và phân trang. */
  updatedAt: string;
}

export interface DocumentPage {
  items: DocumentItem[];
  /** Đếm theo bộ lọc hiện tại, không phải toàn kho. */
  total: number;
  nextCursor: string | null;
}

/** `GET /documents/{id}/open`: tệp tải lên là URL ký 300 giây. */
export interface DocumentOpen {
  url: string;
  type: DocType;
}

/**
 * Trường chung của thân tạo/sửa tài liệu (JSON, hoặc trường `data` của multipart).
 *
 * `folderId` ở `PATCH` có ba nghĩa: vắng là giữ chỗ cũ, `null` là đưa về "Chưa phân loại", `<id>` là
 * chuyển vào thư mục đó. Gộp "vắng" với `null` thì mỗi lần sửa tiêu đề lại dọn tài liệu về gốc.
 */
export interface DocumentFields {
  title?: string;
  category?: Category;
  iconName?: IconName;
  accentName?: AccentName;
  folderId?: string | null;
  summary?: string;
  meta?: string;
  thumbnail?: string;
  updatedAt?: string;
  isPublished?: boolean;
  /** Chỉ ở thân JSON (liên kết ngoài). Multipart suy từ tệp. */
  url?: string;
  type?: DocType;
}

export interface Folder {
  id: string;
  sourceId: string;
  name: string;
  /** `null` là nằm ở gốc của nguồn. */
  parentId: string | null;
  /** Slug ghép từ tổ tiên (`bao-cao-thang/2026-08`), danh sách đã sắp theo nó. */
  path: string;
  sortOrder: number;
  /** Chỉ có khi hỏi kèm `kemSoLuong`. */
  documentsTotal?: number;
  createdAt: string;
  /** UUID người tạo. */
  createdBy: string;
  updatedAt: string;
}

export interface CreateFolderInput {
  name: string;
  /** Mã nguồn; vắng là nguồn mặc định. */
  source?: string;
  /** Vắng là tạo ở gốc. */
  parentId?: string;
}

export interface UpdateFolderInput {
  name?: string;
  sortOrder?: number;
}

export interface Source {
  id: string;
  /** Mã trên dây: `?nguon=` của màn, `?source=` của API. Không đổi được sau khi tạo. */
  code: string;
  name: string;
  description?: string;
  /** Nguồn app di động đọc khi không chọn nguồn nào. */
  isDefault: boolean;
  isManual: boolean;
  isActive: boolean;
  sortOrder: number;
  documentsTotal: number;
  createdAt: string;
  /** UUID người tạo; nguồn `default` nạp sẵn mang UUID nil. */
  createdBy: string;
  updatedAt: string;
}

export interface CreateSourceInput {
  code: string;
  name: string;
  description?: string;
  isManual?: boolean;
}

export interface UpdateSourceInput {
  name?: string;
  description?: string;
  sortOrder?: number;
}

export interface QueryDocuments {
  limit: number;
  cursor?: string;
  category?: Category;
  q?: string;
  /** Mã nguồn; vắng là nguồn mặc định. */
  source?: string;
  /** Vắng là **mọi** thư mục, không phải gốc. Không đi cùng `chuaPhanLoai` (400). */
  folder?: string;
  /** Kèm thư mục con; chỉ có nghĩa khi có `folder`. */
  deep?: boolean;
  /** Chỉ tài liệu chưa nằm trong thư mục nào. */
  chuaPhanLoai?: boolean;
}

/** Tệp người dùng chọn — kiểu cấu trúc để phép tính thuần không cần DOM. */
export interface TepChon {
  name: string;
  size: number;
}
